<script setup lang="ts">
/**
 * One glyph per stat, drawn rather than fetched.
 *
 * Same argument as the radar bezel, the title badges, the key caps and the
 * daily-quest card's metric glyphs: this renders at ~14 CSS px, the game runs at
 * `devicePixelRatio`, and any fixed raster at fourteen device pixels is soft on
 * half the machines that play it. Stroked in `currentColor`, so an icon takes the
 * colour of whatever row it sits in and there is no second palette to keep in
 * step with the panel's.
 *
 * **An unknown key falls back to a reticle rather than to nothing.** A stat list
 * with a hole in it reads as a broken screen; a generic mark reads as a stat
 * whose icon has not been drawn yet, which is what it is. That is the same rule
 * `DailyQuestBox` follows for an unrecognised metric and the same rule a prop
 * with `model: null` follows for its placeholder block.
 *
 * The set is deliberately *shapes*, not letters or tinted squares: at this size
 * the only channel that survives is silhouette, so every glyph differs in
 * outline from every other one — a shield is not a chevron is not a dial. Where
 * two stats are the same kind of thing (top speed and reverse) they differ in
 * direction, which is the one further channel a 16 px outline has.
 */
import { computed } from 'vue';

/** Path data on a 16 × 16 box, stroked. Arcs rather than `<circle>`, so the
 *  whole set is one shape of thing to render. */
const GLYPHS: Record<string, string[]> = {
  // A shield. Survivability's own mark, and the plainest thing on the sheet.
  maxHealth: ['M8 1.8 13 3.5v4.3c0 3.1-2.2 5.2-5 6.4-2.8-1.2-5-3.3-5-6.4V3.5z'],
  // The same shield, banded — armour is plate, and plate is layered.
  armour: ['M8 1.8 13 3.5v4.3c0 3.1-2.2 5.2-5 6.4-2.8-1.2-5-3.3-5-6.4V3.5z', 'M3.2 7.6h9.6'],
  // A dial with a needle: the one instrument everybody reads as speed.
  maxForwardSpeed: ['M2.6 11.6a6 6 0 1 1 10.8 0', 'M8 11.2 10.9 6.8'],
  // A rising curve under an arrowhead — a rate of change rather than a rate.
  acceleration: ['M2.5 13.2C6.2 13.2 9 10.4 10.6 4.2', 'M8.4 4.8 10.8 2.4l1.6 2.8'],
  // A hull swinging: an open ring with the arrow on its end.
  hullTurnRate: ['M3.1 9.8a5.2 5.2 0 1 1 9.7-2.6', 'M10.4 5.1 13 6.9l1-2.7'],
  // Backwards into something: the arrow points at the wall behind the tank.
  maxReverseSpeed: ['M12.6 3.6 8.1 8l4.5 4.4', 'M3.2 3.4v9.2'],
  // A four-point burst. The one glyph shared with the quest card, on purpose:
  // it means the same thing in both places.
  damage: ['M8 1.5 9.5 6.5 14.5 8 9.5 9.5 8 14.5 6.5 9.5 1.5 8 6.5 6.5Z'],
  damagePerSecond: ['M8 1.5 9.5 6.5 14.5 8 9.5 9.5 8 14.5 6.5 9.5 1.5 8 6.5 6.5Z'],
  // A clock. Reload is the only stat here measured in seconds.
  reloadSeconds: ['M14 8a6 6 0 1 1-12 0a6 6 0 1 1 12 0', 'M8 4.6V8l2.4 1.6'],
  // A measured span, ticked at both ends.
  range: ['M2.6 8h10.8', 'M2.6 5.4v5.2', 'M13.4 5.4v5.2'],
  // A round leaving, with the air behind it.
  muzzleVelocity: ['M7.6 5.2 13.2 8l-5.6 2.8z', 'M1.6 8h4.2', 'M2.8 5.4h2.4', 'M2.8 10.6h2.4'],
  // A gun tube under the arc it sweeps.
  turretSlewRate: ['M8 13.4V5.2', 'M3.7 6.8a5.6 5.6 0 0 1 8.6 0'],
};

/** The fallback: a reticle. Something is being measured; we have not drawn what. */
const FALLBACK = ['M11.2 8a3.2 3.2 0 1 1-6.4 0a3.2 3.2 0 1 1 6.4 0', 'M8 1.5v2.2', 'M8 12.3v2.2', 'M1.5 8h2.2', 'M12.3 8h2.2'];

const props = defineProps<{ stat: string }>();

const glyph = computed(() => GLYPHS[props.stat] ?? FALLBACK);
</script>

<template>
  <svg class="stat-icon" viewBox="0 0 16 16" aria-hidden="true">
    <path v-for="(d, i) in glyph" :key="i" :d="d" />
  </svg>
</template>

<style scoped>
.stat-icon {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
